import {
  BOOLEAN_SELECT_LIST_ITEMS,
  Card,
  Checkbox,
  CrudMode,
  DataArray,
  DataDisplay,
  Grid,
  GridBreak,
  Stack,
  ValidationErrors,
  formatBoolean,
  formatDate,
  formatMinutes,
  formatPhoneNumber,
  pn,
  useElementBreakpoints,
} from "@compass/core";
import { DatePicker, DurationInput, Input, PhoneInput, Select } from "components";
import { Developer, DeveloperEditResource, DeveloperViewResource } from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import ApiService from "services/apiService";

export const BasicInfo = (props: {
  crudMode: CrudMode;
  developer: Developer;
  setDeveloper: (value: Developer) => void;
  viewResource: DeveloperViewResource;
  editResource: DeveloperEditResource;
  validationErrors: ValidationErrors;
}) => {
  const { breakpointsRef, smallFieldBreakpoints, largeFieldBreakpoints, fullFieldBreakpoints } =
    useElementBreakpoints();

  const properties = props.viewResource.properties || [];

  const nameField = () => {
    const propertyName = pn<Developer>("name");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.developer.name}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.developer.name}
        onChange={(e) =>
          props.setDeveloper(
            produce(props.developer, (draft) => {
              draft.name = e.target.value;
            })
          )
        }
      />
    );
  };

  const genderField = () => {
    const propertyName = pn<Developer>("genderId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.developer.gender?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.genders?.find((x) => x.id === props.developer.genderId)}
        options={props.editResource.genders}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setDeveloper(
            produce(props.developer, (draft) => {
              draft.genderId = value?.id;
            })
          )
        }
      />
    );
  };

  const developerTypeField = () => {
    const propertyName = pn<Developer>("developerTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.developer.developerType?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.developerTypes?.find((x) => x.id === props.developer.developerTypeId)}
        options={props.editResource.developerTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setDeveloper(
            produce(props.developer, (draft) => {
              draft.developerTypeId = value?.id;
            })
          )
        }
      />
    );
  };

  const dateOfBirthField = () => {
    const propertyName = pn<Developer>("dateOfBirth");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.developer.dateOfBirth)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.developer.dateOfBirth}
        onChange={(value) =>
          props.setDeveloper(
            produce(props.developer, (draft) => {
              draft.dateOfBirth = value;
            })
          )
        }
      />
    );
  };

  const workDurationField = () => {
    const propertyName = pn<Developer>("workDurationInMinutes");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatMinutes(props.developer.workDurationInMinutes)}</DataDisplay>
    ) : (
      <DurationInput
        errors={props.validationErrors[propertyName]}
        value={props.developer.workDurationInMinutes}
        onChange={(value) =>
          props.setDeveloper(
            produce(props.developer, (draft) => {
              draft.workDurationInMinutes = value;
            })
          )
        }
      />
    );
  };

  const hourlySalaryField = () => {
    const propertyName = pn<Developer>("hourlySalary");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.developer.hourlySalary}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.developer.hourlySalary}
        onChange={(e) =>
          props.setDeveloper(
            produce(props.developer, (draft) => {
              draft.hourlySalary = parseFloat(e.target.value);
            })
          )
        }
      />
    );
  };

  const phoneField = () => {
    const propertyName = pn<Developer>("phone");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatPhoneNumber(props.developer.phone)}</DataDisplay>
    ) : (
      <PhoneInput
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.developer.phone}
        onChange={(value) =>
          props.setDeveloper(
            produce(props.developer, (draft) => {
              draft.phone = value;
            })
          )
        }
      />
    );
  };

  const isMarriedField = () => {
    const propertyName = pn<Developer>("isMarried");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.developer.isMarried)}</DataDisplay>
    ) : (
      <Stack height="100%" justifyContent={"center"}>
        <Checkbox
          label={property?.displayName}
          checked={props.developer.isMarried}
          onChange={(e) =>
            props.setDeveloper(
              produce(props.developer, (draft) => {
                draft.isMarried = e.target.checked;
              })
            )
          }
        />
      </Stack>
    );
  };

  const hasChildrenField = () => {
    const propertyName = pn<Developer>("hasChildren");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={`${property?.displayName} (Nullable Boolean)`}>
        {formatBoolean(props.developer.hasChildren)}
      </DataDisplay>
    ) : (
      <Select
        label={`${property?.displayName} (Nullable Boolean)`}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.developer.hasChildren)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setDeveloper(
            produce(props.developer, (draft) => {
              draft.hasChildren = value?.id;
            })
          )
        }
      />
    );
  };

  const studentField = () => {
    const propertyName = pn<Developer>("studentId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={`${property?.displayName} (Single + Backend Fetch)`}>
        {props.developer.student && (
          <>
            {props.developer.student?.lastName}, {props.developer.student?.firstName}
          </>
        )}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={`${property?.displayName} (Single + Backend Fetch)`}
        errors={props.validationErrors[propertyName]}
        value={props.developer.student}
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        onChange={(e, value) =>
          props.setDeveloper(
            produce(props.developer, (draft) => {
              draft.studentId = value?.id;
              draft.student = value;
            })
          )
        }
        fetchFunction={(searchString: string) => ApiService.search.students(searchString)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const collegeField = () => {
    const propertyName = pn<Developer>("colleges");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={`${property?.displayName} (Multiple)`}>
        <DataArray value={props.developer.colleges?.map((x) => x.name)} />
      </DataDisplay>
    ) : (
      <Select
        multiple
        label={`${property?.displayName} (Multiple)`}
        required={property?.required}
        errors={props.validationErrors[propertyName]}
        value={props.developer.colleges}
        options={props.editResource.colleges}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setDeveloper(
            produce(props.developer, (draft) => {
              draft.colleges = values;
            })
          )
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const collegeField2 = () => {
    const propertyName = pn<Developer>("colleges");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={`${property?.displayName} (Multiple + Backend Fetch)`}>
        <DataArray value={props.developer.colleges?.map((x) => x.name)} />
      </DataDisplay>
    ) : (
      <Select
        multiple
        label={`${property?.displayName} (Multiple + Backend Fetch)`}
        required={property?.required}
        errors={props.validationErrors[propertyName]}
        value={props.developer.colleges}
        getOptionLabel={(option) => option.name}
        onChange={(e, values) =>
          props.setDeveloper(
            produce(props.developer, (draft) => {
              draft.colleges = values;
            })
          )
        }
        //fetchFunction={(searchString: string) => ApiService.simpleSearch.getSimpleSearchColleges(searchString)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const noteField = () => {
    const propertyName = pn<Developer>("note");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName} type="textarea">
        {props.developer.note}
      </DataDisplay>
    ) : (
      <Input
        multiline
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.developer.note}
        onChange={(e) =>
          props.setDeveloper(
            produce(props.developer, (draft) => {
              draft.note = e.target.value;
            })
          )
        }
      />
    );
  };

  return (
    <Card header={"Basic Info"}>
      <Grid ref={breakpointsRef} container spacing="sm">
        <Grid item xs={smallFieldBreakpoints}>
          {nameField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {genderField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {developerTypeField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {dateOfBirthField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {workDurationField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {hourlySalaryField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {phoneField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {isMarriedField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {hasChildrenField()}
        </Grid>

        <Grid item xs={smallFieldBreakpoints}>
          {studentField()}
        </Grid>

        <GridBreak />

        <Grid item xs={largeFieldBreakpoints}>
          {collegeField()}
        </Grid>

        <Grid item xs={largeFieldBreakpoints}>
          {collegeField2()}
        </Grid>

        <Grid item xs={fullFieldBreakpoints}>
          {noteField()}
        </Grid>
      </Grid>
    </Card>
  );
};
